import { Flex, TextInput } from '@tremor/react';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import s from './AccountSelection.module.scss';
import { restartConnectorCreationCloudFunction } from '@/firebase/init';
import { useState } from 'react';
import { SelectableAccount } from '@/types/Connectors';
import { useEditAdAccounts } from '@/hooks/useEditAdAccounts';
import { useOrganizationStore } from '@/stores/organizationStore';
import { assert } from '@/utils/asserts';
import { ConnectorTitle } from '../ConnectorTitle/ConnectorTitle';
import { SearchBar } from '../../Inputs/SearchBar/SearchBar';
import { ConnectionSteps } from '@/components/ConnectionSteps/ConnectionSteps';
import { ConnectorSubtitle } from '../ConnectorSubtitle/ConnectorSubtitle';
import { OutlinedButton } from '../../Buttons/OutlinedButton/OutlinedButton';
import { SingleItem } from '../../Modals/TagsSelection/SingleItem';
import { BottomActions } from '../BottomActions/BottomActions';

interface Props {
  title: string;
  connectionSteps: readonly string[];
  selectedIcon: string;
  unselectedIcon: string;
  displayAccountIdParser?: (id: string) => string;
}

export function AccountSelection<
  AccountType extends SelectableAccount = SelectableAccount,
>({
  connectionSteps,
  title,
  selectedIcon,
  unselectedIcon,
  displayAccountIdParser,
}: Props) {
  const {
    connector,
    setSearchPhrase,
    setData,
    displayData,
    isSetup,
    isLoading,
    onAdAccountClick,
    register,
    onUpdate,
    errors,
    isConnectorFailure,
    workspaceId,
    onUpdateName,
    connectorStatus,
    isInitialSetup,
  } = useEditAdAccounts<AccountType>();

  const queryParams = new URLSearchParams(location.search);
  const stateParam = queryParams.get('status');
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  const [isRestartingConnectorCreation, setIsRestartingConnectorCreation] =
    useState(false);
  const organizationId = selectedOrganization?.id;
  assert(organizationId, 'Organization not found');

  const isConnectorChangingActivity =
    connectorStatus === 'Activating' || connectorStatus === 'Deactivating';

  let content = (
    <div className={s.wrapper}>
      <ConnectorTitle isInitialSetup={isInitialSetup} connectorStatus={connectorStatus}>
        {title}
      </ConnectorTitle>
      {isSetup ? (
        <ConnectionSteps
          style={{ marginBottom: '2.5rem' }}
          steps={connectionSteps}
          currentStep={2}
        />
      ) : null}
      <ConnectorSubtitle style={{ textTransform: 'uppercase', marginTop: '2rem' }}>
        Change connector name
      </ConnectorSubtitle>
      <Flex style={{ marginBottom: '1.5rem', gap: '1rem', maxWidth: '30rem' }}>
        <TextInput
          disabled={isLoading}
          {...register('name')}
          error={!!errors.name}
          errorMessage={errors.name?.message}
        />
        <OutlinedButton disabled={isLoading} onClick={onUpdateName}>
          Update Name
        </OutlinedButton>
      </Flex>
      <hr style={{ marginTop: '1.5rem', marginBottom: '1rem' }} />
      <ConnectorSubtitle style={{ textTransform: 'uppercase' }}>
        Select advertising accounts to link
      </ConnectorSubtitle>
      <SearchBar
        cancelButton={true}
        style={{ marginBottom: '1rem' }}
        disabled={isLoading}
        placeholder="Find Account"
        onSubmit={(val) => {
          setSearchPhrase(val.trim());
        }}
      />
      <ul className={s.adAccountsContainer}>
        {displayData.map((adAccount) => (
          <SingleItem
            connectorId={connector?.id}
            status={adAccount.status}
            disabled={isLoading || isConnectorChangingActivity}
            selectedIcon={selectedIcon}
            unselectedIcon={unselectedIcon}
            className="adAccountButton"
            onClick={() => setData((prev) => onAdAccountClick(prev, adAccount.id))}
            isSelected={adAccount.selected}
            isLoading={!adAccount.status.includes('SUCCESS')}
            accountId={adAccount.id}
            key={adAccount.id}
            annotation={adAccount.annotation}
          >
            {adAccount.name} (ID:{' '}
            {displayAccountIdParser ? displayAccountIdParser(adAccount.id) : adAccount.id}
            )
          </SingleItem>
        ))}
      </ul>
      <BottomActions
        disabledReconnect={isInitialSetup || isConnectorChangingActivity}
        connectorId={connector?.id}
        isCreating={stateParam === 'success'}
        isLoading={isLoading}
        onUpdate={onUpdate}
      />
    </div>
  );

  if (isConnectorFailure) {
    content = (
      <LoadingIndicator
        isLoading={false}
        wholeMessage="There was an error while creating this connector"
      >
        <OutlinedButton
          onClick={async () => {
            setIsRestartingConnectorCreation(true);
            try {
              await restartConnectorCreationCloudFunction({
                connectorId: connector?.id,
                workspaceId: workspaceId,
                organizationId: organizationId,
              });
            } finally {
              setIsRestartingConnectorCreation(false);
            }
          }}
          disabled={isRestartingConnectorCreation}
        >
          Retry
        </OutlinedButton>
      </LoadingIndicator>
    );
  }

  return content;
}
